"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Weekend Surge & Midweek Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Alibag experiences dramatic 100% weekend sell-outs driven by Mumbai leisure travelers, contrasted with steep midweek occupancy drop-offs. We implement dynamic minimum stay restrictions and value-led weekday packages to balance RevPAR across all 7 days.",
  metricLabel: "Strategy focus",
  metricValue: "Length-of-stay controls & midweek corporate/workation packages"
}, {
  id: 2,
  title: "Aggressive Comp-Set Rate Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Rapid expansion of boutique villas and coastal homestays creates volatile rate volatility across MakeMyTrip and Booking.com. Automated competitive pacing alerts protect premium pricing while capturing high-yield advance bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & rule-based dynamic rate fences"
}, {
  id: 3,
  title: "Direct Conversion vs. OTA Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent costly high-commission dependency on third-party channels for high-spending Mumbai & Pune clientele. We enforce strict multi-channel rate parity and build direct booking engines with bespoke stay incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Metasearch parity protection & direct-guest booking engine yield"
}];
const seasonalitySeasons = [{
  season: "Peak Season (Oct - Mar)",
  badge: "High ADR",
  icon: Sun,
  description: "Dry coastal climate, festive long weekends, and destination weddings. Focus on dynamic yield capping, strict MLOS, and non-refundable deposit terms."
}, {
  season: "Monsoon Surge (Jun - Sep)",
  badge: "Scenic Leisure",
  icon: CloudRain,
  description: "Lush green drive-in getaways from Mumbai & Pune. Leverage experiential monsoon packages, flexible cancellations, and villa buyout bundling."
}, {
  season: "Shoulder Period (Apr - May)",
  badge: "Value Yield",
  icon: Sparkles,
  description: "Pre-monsoon summer breaks and workation travelers. Deploy targeted midweek OTA promotions, extended stay deals, and corporate retreat packages."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapealibag" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Alibag
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            With seamless Ro-Pax ferry and speedboat links from Mumbai, Alibag has transformed into a high-yield leisure micro-market characterized by sharp weekend compression, seasonal monsoon spikes, and rising competition from luxury villas. Sunil Singh engineers precision revenue strategies custom-tailored for Alibag hotels and boutique properties.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Strategy Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mt-12 md:mt-16 rounded-xl border border-border bg-card/60 p-6 sm:p-8 md:p-10 backdrop-blur-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border/70">
            <div>
              <span className="text-xs font-mono font-medium uppercase tracking-wider text-secondary">
                Alibag Market Calendar
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                Seasonal Yield & Demand Playbook
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              Data-backed pricing matrices matched to Mumbai-Alibag connectivity cycles, festival calendars, and monsoon travel patterns.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {seasonalitySeasons.map((seasonItem, idx) => {
            const IconComp = seasonItem.icon;
            return <div key={seasonItem.season} data-index={idx} className="rounded-lg border border-border/70 bg-background/60 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                        <IconComp className="size-5 text-chart-2" />
                      </div>
                      <Badge variant="secondary" className="text-[11px] px-2 py-0.5">
                        {seasonItem.badge}
                      </Badge>
                    </div>
                    <h4 className="font-serif text-base sm:text-lg font-bold text-foreground mb-2">
                      {seasonItem.season}
                    </h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {seasonItem.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Alibag hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}